import { BaseController } from 'controllers/BaseController'
import { OnRequestChangeCallback } from 'services/APIs/BaseAPI'
import { InternalAPI } from 'services/APIs/InternalAPI/InternalAPI'
import {
  CreateDraftQuotationResponseCommandRequest,
  CreateDraftQuotationResponseCommandResponse,
  GetQuotationResponseQueryResponse,
  MoneyDto,
  SaveQuotationResponseCommandRequest,
  SaveQuotationResponseCommandResponse,
} from 'services/APIs/InternalAPI/internal-api.contracts'
import { GetContext } from 'store/storeUtils'

export type QuotationResponseSendPayload = {
  quotationResponsePrices?: Array<{
    bomItemId: string
    price: MoneyDto
  }>
}

interface IProjectQuotationsApi {
  GetQuotationResponse: () => Promise<GetQuotationResponseQueryResponse>

  CreateQuotationResponse: (
    req: CreateDraftQuotationResponseCommandRequest,
  ) => Promise<CreateDraftQuotationResponseCommandResponse>

  SaveQuotationResponse: (
    req: SaveQuotationResponseCommandRequest,
  ) => Promise<SaveQuotationResponseCommandResponse>

  SendQuotationResponseToBuyer: (quotationId: string) => Promise<void>

  DeleteQuotationResponse: (quotationId: string) => Promise<void>

  AcceptQuotationResponsePost: (
    rfqId: string,
    quotationId: string,
    forwardId: string,
    forwardSender: string,
  ) => Promise<void>

  CancelQuotationResponsePost: (
    rfqId: string,
    quotationId: string,
    forwardId: string,
    forwardSender: string,
  ) => Promise<void>
}

class ProjectQuotationsAPI
  extends InternalAPI
  implements IProjectQuotationsApi
{
  constructor(
    partyId: string,
    projectId: string,
    onRequestChange: OnRequestChangeCallback,
  ) {
    super(
      `/api/parties/${partyId}/projects/${projectId}/quotations`,
      onRequestChange,
    )
  }

  public async DeleteQuotationResponse(quotationId: string): Promise<void> {
    return this.DeleteAsync({
      relativePath: `/${quotationId}`,
      id: `DeleteQuotationResponse-${quotationId}`,
    })
  }

  public async GetQuotationResponse(): Promise<GetQuotationResponseQueryResponse> {
    return this.GetAsync<GetQuotationResponseQueryResponse>({
      relativePath: '/quotationResponse',
      id: 'GetQuotationResponse',
    })
  }

  public async CreateQuotationResponse(
    req: CreateDraftQuotationResponseCommandRequest,
  ) {
    return this.PostAsync<CreateDraftQuotationResponseCommandResponse>({
      relativePath: '/quotationResponse/draft',
      id: 'CreateDraftQuotationResponse',
      data: req,
    })
  }

  public async SaveQuotationResponse(req: SaveQuotationResponseCommandRequest) {
    return this.PostAsync<SaveQuotationResponseCommandResponse>({
      relativePath: `/${req.quotationResponseId}/save`,
      id: `SaveQuotationResponse-${req.quotationResponseId}`,
      data: req,
    })
  }

  public async SendQuotationResponseToBuyer(
    quotationId: string,
  ): Promise<void> {
    return await this.PostAsync({
      relativePath: `/${quotationId}/send`,
      id: `SendQuotationResponseToBuyer-${quotationId}`,
    })
  }

  public async AcceptQuotationResponsePost(
    rfqId: string,
    quotationResponseId: string,
    forwardId: string,
    forwardSender: string,
  ): Promise<void> {
    return await this.PostAsync({
      relativePath: `/quotationResponse/${quotationResponseId}/accept`,
      id: `AcceptQuotationResponsePost-${rfqId}-${quotationResponseId}`,
      data: {
        rfqId,
        quotationResponseId: quotationResponseId,
        forwardId,
        forwardSender,
      },
    })
  }

  public async CancelQuotationResponsePost(
    rfqId: string,
    quotationResponseId: string,
    forwardId: string,
    forwardSender: string,
  ): Promise<void> {
    return await this.PostAsync({
      relativePath: `/${quotationResponseId}/cancel`,
      id: `CancelQuotationResponsePost-${rfqId}-${quotationResponseId}`,
      data: {
        rfqId,
        quotationResponseId: quotationResponseId,
        forwardId,
        forwardSender,
      },
    })
  }
}

export class ProjectQuotationsController extends BaseController<IProjectQuotationsApi> {
  constructor() {
    super((onRequestChange) => {
      const { partyId, projectId } = GetContext()
      return new ProjectQuotationsAPI(partyId, projectId, onRequestChange)
    })
  }

  public async GetQuotationResponse(): Promise<GetQuotationResponseQueryResponse> {
    try {
      return await this.api.GetQuotationResponse()
    } catch (error) {
      throw this.HandleError(error)
    }
  }

  public async CreateQuotationResponse(
    req: CreateDraftQuotationResponseCommandRequest,
  ): Promise<CreateDraftQuotationResponseCommandResponse> {
    try {
      return await this.api.CreateQuotationResponse(req)
    } catch (error) {
      throw this.HandleError(error)
    }
  }

  public async SaveQuotationResponse(
    req: SaveQuotationResponseCommandRequest,
  ): Promise<SaveQuotationResponseCommandResponse> {
    try {
      return await this.api.SaveQuotationResponse(req)
    } catch (error) {
      throw this.HandleError(error)
    }
  }

  public async SendQuotationResponseToBuyer(
    quotationId: string,
  ): Promise<void> {
    try {
      return await this.api.SendQuotationResponseToBuyer(quotationId)
    } catch (error) {
      throw this.HandleError(error)
    }
  }

  public async AcceptQuotationResponsePost(
    rfqId: string,
    quotationResponseId: string,
    forwardId: string,
    forwardSender: string,
  ): Promise<void> {
    try {
      return await this.api.AcceptQuotationResponsePost(
        rfqId,
        quotationResponseId,
        forwardId,
        forwardSender,
      )
    } catch (err) {
      throw this.HandleError(err)
    }
  }

  public async CancelQuotationResponsePost(
    rfqId: string,
    quotationResponseId: string,
    forwardId: string,
    forwardSender: string,
  ): Promise<void> {
    try {
      return await this.api.CancelQuotationResponsePost(
        rfqId,
        quotationResponseId,
        forwardId,
        forwardSender,
      )
    } catch (err) {
      throw this.HandleError(err)
    }
  }

  public async DeleteQuotationResponse(
    quotationResponseId: string,
  ): Promise<void> {
    try {
      return await this.api.DeleteQuotationResponse(quotationResponseId)
    } catch (err) {
      throw this.HandleError(err)
    }
  }
}
